import { useState, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import LockIcon from "@mui/icons-material/Lock";
import { Box, Skeleton } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import IosShareIcon from "@mui/icons-material/IosShare";

// 画像を合成する関数
const mergeImages = async (imageUrls) => {
  return new Promise(async (resolve, reject) => {
    try {
      const images = await Promise.all(imageUrls.map(loadImage));
      if (images.length === 0) return reject(new Error("画像がありません"));

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const width = images[0].width;
      const height = images[0].height;
      canvas.width = width;
      canvas.height = height;

      images.forEach((img) => ctx.drawImage(img, 0, 0, width, height));

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob)); // ✅ Blob URL を返す
        } else {
          reject(new Error("画像の生成に失敗しました"));
        }
      }, "image/png");
    } catch (error) {
      reject(error);
    }
  });
};

// 画像をロードする関数
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // CORS 対策
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
  });
};

// 画像をダウンロードする関数
const downloadImage = (imageUrl, fileName = "merged_image.png") => {
  const link = document.createElement("a");
  link.href = imageUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getFileNameFromUrl = (imageUrl) => {
  try {
    const pathname = new URL(imageUrl, window.location.origin).pathname;
    return pathname.split("/").pop() || "reward.jpg";
  } catch (_) {
    return "reward.jpg";
  }
};

const shareImage = async (imageUrl, fileName) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  const file = new File([blob], fileName, {
    type: blob.type || "image/jpeg",
  });

  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      files: [file],
      title: fileName,
    });
    return;
  }

  if (navigator.share) {
    await navigator.share({
      title: fileName,
      url: imageUrl,
    });
    return;
  }

  downloadImage(imageUrl, fileName);
};

// `ImageWithSaveButton` コンポーネント
export default function ImageWithSaveButton({
  imageUrls,
  showDownloadButton = false,
  saveMode = "download",
  saveFileName,
}) {
  const [mergedImage, setMergedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const merge = async () => {
      try {
        setLoading(true);
        setError("");
        const mergedUrl = await mergeImages(imageUrls);
        setMergedImage(mergedUrl);
      } catch (err) {
        setError("報酬の表示ができませんでした");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (showDownloadButton === true) {
      setMergedImage(imageUrls[0]);
      setLoading(false);
    } else if (imageUrls.length > 0) {
      merge();
    } else {
      setLoading(false);
      setMergedImage(null);
    }
  }, [imageUrls, showDownloadButton]);

  // 画像タップでボタン表示/非表示をトグル
  const handleImageTap = () => {
    if (showDownloadButton) {
      setShowButton((prev) => !prev);
    }
  };

  if (!loading && !mergedImage) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="240px"
        width="100%"
        backdropFilter="blur(16px)"
        WebkitBackdropFilter="blur(16px)"
        borderRadius="4px"
        sx={(theme) => ({
          ...theme.components.questRewardBox.paper,
        })}
      >
        <LockIcon
          sx={(theme) => ({
            fontSize: 50,
            ...theme.components.questCard.lockIcon,
          })}
        />
      </Box>
    );
  }

  return (
    <>
      {loading && <Skeleton variant="rectangular" width="100%" height={240} />}
      {!loading && (
        <div
          style={{
            position: "relative",
            display: "inline-block",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
            borderRadius: "6px",
            cursor: showDownloadButton ? "pointer" : "default",
          }}
          onClick={handleImageTap}
        >
          {error && <p style={{ color: "red" }}>{error}</p>}
          {mergedImage && (
            <>
              <img
                src={mergedImage}
                alt="Merged"
                style={{
                  width: "100%",
                  display: "block",
                  borderRadius: "4px",
                  filter: "drop-shadow(0px 0px 5px #666666)",
                }}
              />
              {/* 保存ボタン（タップで表示/非表示） */}
              {showDownloadButton && showButton && (
                <IconButton
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "50%",
                    padding: 8,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
                    transition: "opacity 0.3s ease",
                  }}
                  aria-label={saveMode === "share" ? "share" : "save"}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const fileName = saveFileName || getFileNameFromUrl(mergedImage);
                    if (saveMode === "share") {
                      try {
                        await shareImage(mergedImage, fileName);
                      } catch (err) {
                        console.error("Failed to share image:", err);
                        downloadImage(mergedImage, fileName);
                      }
                      return;
                    }
                    downloadImage(mergedImage, fileName);
                  }}
                >
                  {saveMode === "share" ? <IosShareIcon /> : <FileDownloadIcon />}
                </IconButton>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
